/* An Event's guest list — STORY-016, paged and movable by STORY-017.
 *
 * ── two numbers, and they are not the same number ───────────────────────────
 * "40 guests, 66 people." How many invitations there are and how many covers
 * they add up to answer different questions — who to write to, and how many to
 * cook for. Both come from the server and both describe the WHOLE list: paging
 * is a view concern, and a headcount that changed when somebody clicked "next"
 * would be a defect rather than an approximation.
 *
 * ── the headcount counts who is actually coming ─────────────────────────────
 * `confirmed` and `attended` alike, because somebody who replied and somebody
 * who turned up are equally a head to cater for. The invited and the cancelled
 * are left out of the total and stay ON the list, so a Member can see who has
 * not answered and who dropped out.
 *
 * ── both selects stay on the row ────────────────────────────────────────────
 * Attendance and payment both change on the night, and demoting either to a
 * panel costs a tap in the one flow that is timed. The density problem forty
 * rows create is answered by showing ten of them — decided 2026-08-14 — not by
 * thinning the row.
 */

import {
  ATTENDANCE_STATUSES,
  type Attendance,
  type AttendanceStatus,
  GUESTS_PAGE_SIZES,
  IMPLAUSIBLE_PAX,
  PAYMENT_STATUSES,
  type PaymentStatus,
} from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { useId, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, PersonPicker, TextField } from '../../../shared/ui/index.js';
import { useCreatePerson, usePeople } from '../../people/index.js';
import { useAddGuest, useGuestList, useRemoveGuest, useUpdateGuest } from '../api/index.js';
import { ALL_GUESTS, type GuestPageSize, readPageSize, writePageSize } from '../page-size.js';

/* Explicit, never `t(`guests.status.${status}`)` — `i18n.md` forbids a
 * constructed key because it defeats extraction and unused-key detection. */
const STATUS_LABELS = {
  invited: 'guests.status.invited',
  confirmed: 'guests.status.confirmed',
  attended: 'guests.status.attended',
  cancelled: 'guests.status.cancelled',
} as const satisfies Record<AttendanceStatus, MessageKey>;

const PAYMENT_LABELS = {
  unpaid: 'guests.payment.unpaid',
  paid: 'guests.payment.paid',
  comped: 'guests.payment.comped',
  refunded: 'guests.payment.refunded',
} as const satisfies Record<PaymentStatus, MessageKey>;

export interface GuestListProps {
  readonly organisationId: string;
  readonly eventId: string;
}

export function GuestList({ organisationId, eventId }: GuestListProps) {
  const t = useTranslate();
  const sizeId = useId();

  /* Remembered across Events, which is why it is stored rather than held in
   * the URL — a guest list lives inside one Event's screen. */
  const [pageSize, setPageSize] = useState<GuestPageSize>(readPageSize);
  const [page, setPage] = useState(1);

  const query = { limit: pageSize, offset: (page - 1) * pageSize };
  const { data, error, isLoading } = useGuestList(organisationId, eventId, query);

  const add = useAddGuest(organisationId, eventId, query);
  const createPerson = useCreatePerson(organisationId);

  const [pickerQuery, setPickerQuery] = useState('');
  /* Set when an add turned out to be somebody already invited. Declared with
   * the other state and not beside the handler that sets it: the error branch
   * below returns early, and a hook after it is a hook React does not count. */
  const [alreadyInvited, setAlreadyInvited] = useState<string | null>(null);
  const { data: book } = usePeople(organisationId, {
    ...(pickerQuery.trim() === '' ? {} : { search: pickerQuery.trim() }),
    limit: 8,
  });

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  const attendances = data?.attendances ?? [];
  const guests = data?.guests ?? 0;
  const pax = data?.pax ?? 0;
  const payments = data?.payments ?? { unpaid: 0, paid: 0, comped: 0, refunded: 0 };

  const firstOnPage = (page - 1) * pageSize + 1;
  const lastPage = Math.max(1, Math.ceil(guests / pageSize));

  /* Removing the last guest on the last page leaves the page number past the
   * end of the list, and the pager was rendered only when the list was longer
   * than a page — so the controls that would have got you back disappeared at
   * the same moment. The header read "Showing 11–10 of 10 · page 2 of 1" over
   * an empty list, with nothing to click.
   *
   * Corrected during render rather than in an effect: an effect would paint
   * the impossible page first. Guarded on `data`, because an in-flight first
   * load reports zero guests and would drag a deliberate page back to one. */
  if (data && page > lastPage) setPage(lastPage);

  const invited = new Set(attendances.map((attendance) => attendance.person.id));

  /**
   * Somebody added lands at the end of the list, which may not be this page.
   *
   * Without the jump the screen does not change at all and the add reads as
   * having failed — STORY-017 names this. The new row is last because the list
   * is ordered by when it was created, so its page is the last page of the
   * list that now includes it.
   *
   * The total comes from the response rather than from `guests + 1`, because
   * the add may not have added anybody: the picker can only exclude the guests
   * on the page it can see, so a Member on page one may well pick somebody
   * already invited on page two. The server answers that with the list
   * unchanged, and `guests + 1` would then send them to a page one past the
   * end — empty, which STORY-017 says "reads as the guests having gone".
   */
  const followTheNewGuest = (list: { guests: number }, name: string) => {
    if (list.guests === guests) {
      /* Said out loud. Doing nothing at all is indistinguishable from a
       * failure, and the guest is on a page they are not looking at. */
      setAlreadyInvited(name);
      return;
    }
    setAlreadyInvited(null);
    setPage(Math.max(1, Math.ceil(list.guests / pageSize)));
  };

  return (
    <section className="settings__section">
      <h2>{t('guests.heading')}</h2>

      {/* ICU, not a branch: English has two plural forms and Turkish takes one
          after a number, so an `if (n === 1)` is wrong in both directions at
          once. A live region, so a screen reader hears the headcount change
          when a guest confirms. */}
      <p className="field__hint" role="status">
        {isLoading ? t('common.loading') : t('guests.totals', { guests, pax })}
      </p>

      {/* Counts, never amounts. Payment status is a flag and the money is a
          Transaction — two places holding the same figure is how the source's
          totals stopped agreeing with each other. Words rather than colour, so
          it survives both a monochrome screen and a screen reader. */}
      {!isLoading && guests > 0 ? (
        <p className="field__hint" role="status">
          {t('guests.payments', payments)}
        </p>
      ) : null}

      <PersonPicker
        label={t('guests.add_label')}
        emptyLabel={t('guests.no_match')}
        resultsLabel={(count) => t('guests.results', { count })}
        createLabel={(typed) => t('guests.create', { name: typed })}
        hint={t('guests.add_hint')}
        error={
          createPerson.error instanceof ApiError &&
          createPerson.error.code === 'entitlement_exceeded'
            ? t('guests.contact_cap')
            : add.error instanceof ApiError && add.error.code === 'not_found'
              ? t('guests.gone')
              : add.error || createPerson.error
                ? t('error.unexpected')
                : undefined
        }
        people={(book?.people ?? [])
          .filter((person) => !invited.has(person.id))
          .map((person) => ({
            id: person.id,
            displayName: person.displayName,
            ...((person.phone ?? person.email)
              ? { detail: person.phone ?? person.email ?? undefined }
              : {}),
          }))}
        query={pickerQuery}
        onQueryChange={setPickerQuery}
        onPick={(personId) =>
          add.mutate(
            { personId },
            {
              onSuccess: (list) =>
                followTheNewGuest(
                  list,
                  book?.people.find((p) => p.id === personId)?.displayName ?? '',
                ),
            },
          )
        }
        onCreate={(displayName) =>
          createPerson.mutate(
            { displayName },
            {
              /* Created AND invited. Adding somebody to the book and then
                 having to find them in it is the half-finished version. */
              onSuccess: (person) =>
                add.mutate(
                  { personId: person.id },
                  { onSuccess: (list) => followTheNewGuest(list, person.displayName) },
                ),
            },
          )
        }
        busy={add.isPending || createPerson.isPending}
      />

      {alreadyInvited ? (
        <Callout tone="info">{t('guests.already_invited', { name: alreadyInvited })}</Callout>
      ) : null}

      {!isLoading && attendances.length > 0 ? (
        <ul className="row-list row-list--tight">
          {attendances.map((attendance) => (
            <GuestRow
              key={attendance.id}
              attendance={attendance}
              organisationId={organisationId}
              eventId={eventId}
              query={query}
            />
          ))}
        </ul>
      ) : null}

      {/* Two different empty states. "Nobody invited yet" and "invited, nobody
          has replied" are different facts about an Event, and the reference
          workbook's inability to tell them apart is called out in the story. */}
      {!isLoading && guests === 0 ? <p className="field__hint">{t('guests.empty')}</p> : null}
      {!isLoading && guests > 0 && pax === 0 ? (
        <p className="field__hint">{t('guests.none_confirmed')}</p>
      ) : null}

      {guests > 0 ? (
        <div className="filters">
          <div className="field">
            <label className="field__label" htmlFor={sizeId}>
              {t('guests.page_size')}
            </label>
            <select
              id={sizeId}
              className="field__control"
              value={String(pageSize)}
              onChange={(changed) => {
                const next = Number(changed.target.value) as GuestPageSize;
                setPageSize(next);
                writePageSize(next);
                /* Back to the first page: page four of ten-at-a-time is past
                   the end of a hundred-at-a-time, and landing on an empty page
                   reads as the guests having gone. */
                setPage(1);
              }}
            >
              {GUESTS_PAGE_SIZES.map((size) => (
                <option key={size} value={String(size)}>
                  {t('guests.page_size_n', { count: size })}
                </option>
              ))}
              <option value={String(ALL_GUESTS)}>{t('guests.page_size_all')}</option>
            </select>
          </div>

          <p className="field__hint" role="status">
            {t('guests.showing', {
              from: firstOnPage,
              to: firstOnPage + attendances.length - 1,
              total: guests,
              page,
              pages: lastPage,
            })}
          </p>

          {/* `lastPage`, not `guests > pageSize`: the two agree except in the
              case that matters, where a removal has just made the list fit on
              one page and the Member is standing on the second. */}
          {lastPage > 1 || page > 1 ? (
            <div className="btn-row">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('guests.previous')}
              </Button>
              <Button variant="ghost" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                {t('guests.next')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function GuestRow({
  attendance,
  organisationId,
  eventId,
  query,
}: {
  attendance: Attendance;
  organisationId: string;
  eventId: string;
  query: { limit: number; offset: number };
}) {
  const t = useTranslate();
  const statusId = useId();
  const paymentId = useId();
  const update = useUpdateGuest(organisationId, eventId, query);
  const remove = useRemoveGuest(organisationId, eventId, query);

  /* Which control the in-flight write came from.
   *
   * One mutation serves three controls, so `update.error` alone cannot say
   * which of them failed — a refused payment status would have shown "that
   * headcount was not saved" under the Pax field, about a number nobody
   * touched. */
  const [changing, setChanging] = useState<'pax' | 'status' | 'payment' | null>(null);

  const [draft, setDraft] = useState(String(attendance.pax));

  /* Resynced when the row changes underneath — the same lesson as the Event
   * status control, where a refetch left a stale value armed to be saved. */
  const [seen, setSeen] = useState(attendance.pax);
  if (seen !== attendance.pax) {
    setSeen(attendance.pax);
    setDraft(String(attendance.pax));
  }

  const parsed = Number(draft);
  const valid = Number.isInteger(parsed) && parsed >= 1;
  const implausible = valid && parsed > IMPLAUSIBLE_PAX;

  const savePax = (pax: number) => {
    setChanging('pax');
    update.mutate(
      { attendanceId: attendance.id, pax },
      /* Put back to what is stored. Leaving a rejected number in the field
       * shows a headcount the server does not have. */
      { onError: () => setDraft(String(attendance.pax)) },
    );
  };

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">{attendance.person.displayName}</span>
      </span>

      <span className="row__actions">
        <div className="field">
          <label className="field__label" htmlFor={statusId}>
            {t('guests.status_short')}
          </label>
          {/* On the row, not behind a panel: this changes on the night, in the
              one flow that is timed. Any status from any status — a cancelled
              guest who reappears goes straight back. */}
          <select
            id={statusId}
            className="field__control"
            aria-label={t('guests.status_label', { name: attendance.person.displayName })}
            value={attendance.status}
            disabled={update.isPending}
            onChange={(changed) => {
              setChanging('status');
              update.mutate({
                attendanceId: attendance.id,
                status: changed.target.value as AttendanceStatus,
              });
            }}
          >
            {ATTENDANCE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_LABELS[status])}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={paymentId}>
            {t('guests.payment_short')}
          </label>
          {/* Its own control, next to the attendance one and independent of
              it: a guest who cancels may still be owed a refund, and one who
              attended may still owe. Any of the four from any of the others —
              a mis-click is correctable without ceremony. */}
          <select
            id={paymentId}
            className="field__control"
            aria-label={t('guests.payment_label', { name: attendance.person.displayName })}
            value={attendance.paymentStatus}
            disabled={update.isPending}
            onChange={(changed) => {
              setChanging('payment');
              update.mutate({
                attendanceId: attendance.id,
                paymentStatus: changed.target.value as PaymentStatus,
              });
            }}
          >
            {PAYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(PAYMENT_LABELS[status])}
              </option>
            ))}
          </select>
        </div>

        <TextField
          label={t('guests.pax_short')}
          aria-label={t('guests.pax_label', { name: attendance.person.displayName })}
          type="number"
          inputMode="numeric"
          min={1}
          value={draft}
          disabled={update.isPending}
          onChange={(changed) => setDraft(changed.target.value)}
          onBlur={() => {
            /* Saved on blur rather than on every keystroke: typing "12" passes
               through "1", and a save per character would write a headcount
               nobody meant and put it in the totals on the way past. */
            if (!valid || parsed === attendance.pax) {
              setDraft(String(attendance.pax));
              return;
            }
            savePax(parsed);
          }}
          error={
            draft.trim() !== '' && !valid
              ? t('guests.pax_invalid')
              : update.error && changing === 'pax'
                ? t('guests.pax_failed')
                : undefined
          }
        />

        <Button
          variant="ghost"
          busy={remove.isPending}
          onClick={() => remove.mutate(attendance.id)}
        >
          {t('guests.remove')}
        </Button>
      </span>

      {/* The select is bound to what the server holds, so a refusal already
          snaps it back — this says why it moved. */}
      {update.error && changing === 'payment' ? (
        <div className="row__expand">
          <Callout tone="danger">
            {update.error instanceof NetworkError ? t('error.network') : t('guests.payment_failed')}
          </Callout>
        </div>
      ) : null}

      {remove.error ? (
        <div className="row__expand">
          <Callout tone="danger">
            {remove.error instanceof NetworkError ? t('error.network') : t('guests.remove_failed')}
          </Callout>
        </div>
      ) : null}

      {implausible ? (
        <div className="row__expand">
          {/* Warns, never blocks. No number is definitely wrong — a wedding is
              four figures — and a mistyped one silently inflates every
              headcount downstream. */}
          <Callout tone="info">{t('guests.pax_implausible', { pax: parsed })}</Callout>
        </div>
      ) : null}
    </li>
  );
}
